/* eslint-disable max-len */
/* eslint-disable max-len */
import React, { useMemo, useState } from 'react';
import {
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CContainer,
  CFormLabel,
  CRow,
  CSpinner,
  CAlert,
  CBadge
} from '@coreui/react';
import { useTranslation } from '../../../hooks/useTranslation';
import { getCustomerAgreementDropdown, getCustomerAgreement } from '../../../services/endpoints';
import { formatDateOnly } from '../../../helpers/dateHelper';
import './CustomerAgreementReport.scss';
import SearchableSelect from '../../../components/SearchableSelect/SearchableSelect';

const escapeHtml = (value) => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

const sanitizeImageSrc = (value) => {
  const src = String(value ?? '').trim();
  if (!src) return '';

  if (src.startsWith('data:image/')) return src;
  if (src.startsWith('blob:')) return src;
  if (src.startsWith('/')) return src;

  try {
    const parsed = new URL(src, window.location.origin);
    if (parsed.protocol === 'http:' || parsed.protocol === 'https:') {
      return parsed.toString();
    }
  } catch (e) {
    return '';
  }

  return '';
};

const formatDensityDecimal = (value) => {
  if (value === null || value === undefined || value === '') return '';

  const numeric = Number(String(value).trim().replace(',', '.'));
  if (!Number.isFinite(numeric)) return String(value);

  const scaled = Math.trunc(Math.abs(numeric));
  const sign = numeric < 0 ? '-' : '';
  const whole = Math.trunc(scaled / 100);
  const decimal = scaled % 100;

  if (decimal === 0) return `${sign}${whole}`;

  return `${sign}${whole},${String(decimal).padStart(2, '0')}`;
};

function CustomerAgreementReport() {
  const { t } = useTranslation();

  const [loadingDropdown, setLoadingDropdown] = useState(false);
  const [dropdownItems, setDropdownItems] = useState([]);
  const [selectedOrderGuid, setSelectedOrderGuid] = useState('');

  const [loadingReport, setLoadingReport] = useState(false);
  const [reportRows, setReportRows] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [error, setError] = useState('');

  const fetchDropdownByOrderNo = async (searchTerm) => {
    const trimmedSearchTerm = (searchTerm || '').trim();

    if (!trimmedSearchTerm) {
      setDropdownItems([]);
      return;
    }

    try {
      setLoadingDropdown(true);
      setError('');
      const res = await getCustomerAgreementDropdown({ OrderNo: trimmedSearchTerm });
      const data = res?.data || res;
      const items = data?.AgreementDropdownInfos || [];
      setDropdownItems(items);
    } catch (e) {
      console.error('CustomerAgreement dropdown error:', e);
      setError('Sözleşme listesi yüklenirken hata oluştu');
      setDropdownItems([]);
    } finally {
      setLoadingDropdown(false);
    }
  };

  const handleGetReport = async () => {
    if (!selectedOrderGuid) return;
    try {
      setLoadingReport(true);
      setError('');
      const res = await getCustomerAgreement({ OrderId: selectedOrderGuid });
      const data = res?.data || res;
      const list = data?.CustomerAgreementInfoList || [];
      setReportRows(list);
      if (list.length === 0) {
        setError(t('noDataFound') || 'Seçilen sözleşme için veri bulunamadı');
      }
    } catch (e) {
      console.error('CustomerAgreement fetch error:', e);
      setError(t('errorLoadingReport') || 'Rapor yüklenirken hata oluştu');
      setReportRows([]);
    } finally {
      setLoadingReport(false);
    }
  };

  const agreement = useMemo(() => (reportRows && reportRows.length > 0 ? reportRows[0] : null), [reportRows]);

  const dropdownOptions = useMemo(() => {
    return (dropdownItems || []).map(it => ({
      value: it.OrderGuid,
      text: [it.OrderNo, it.CustomerCode, it.CustomerName, it.WeaveCode].filter(Boolean).join(' - '),
    }));
  }, [dropdownItems]);

  const fmtDate = (d) => {
    if (!d) return '';
    return formatDateOnly(d);
  };

  const fmtNum = (n) => {
    if (n == null || n === '') return '';
    try { return Number(n).toLocaleString('tr-TR'); } catch (e) { return String(n); }
  };

  const fmtPrice = (n) => {
    if (n == null || n === '') return '';
    try {
      const val = Number(n) / 100;
      return val.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    } catch (e) { return String(n); }
  };

  const joinYarns = (list) => {
    const seen = new Set();
    const yarns = [];

    (list || []).forEach((item) => {
      const yarnGuid = item?.PrdYarnGuid || item?.YarnGuid || item?.YarnId || item?.yarnGuid || '';
      const colorGuid = item?.PrdColorGuid || item?.ColorGuid || item?.ColorId || item?.colorGuid || '';
      const display = escapeHtml([item?.YarnCode, item?.YarnName].filter(Boolean).join(' ').trim());

      if (!display) return;

      const key = (yarnGuid || colorGuid)
        ? `${yarnGuid}-${colorGuid}`
        : `text-${display.toLowerCase()}`;

      if (seen.has(key)) return;
      seen.add(key);
      yarns.push(display);
    });

    return yarns
      .map((yarn, index) => `${index + 1}. ${yarn}`)
      .join('<br/>');
  };

  const buildTemplateHTML = () => {
    const logoUrl = `${window.location.origin}/logo.jpg`;
    const today = new Date();
    const tarihStr = today.toLocaleDateString('tr-TR');
    const customerName = agreement?.CustomerName || '';
    const contactName = agreement?.ContactName || '';
    const articleNo = agreement?.CustomerCode || '';
    const weaveCode = agreement?.WeaveCode || '';
    const weaveType = agreement?.WeaveTypeName || '';
    const warpYarn = joinYarns(agreement?.WarpYarnInfoList);
    const weftYarn = joinYarns(agreement?.WeftYarnInfoList);
    const warpDensity = formatDensityDecimal(agreement?.WarpDensity);
    const weftDensity = formatDensityDecimal(agreement?.WeftDensity);
    const reedWidth = agreement?.ReedWidth || '';
    const quantity = fmtNum(agreement?.ProductQuantity);
    const price = [fmtPrice(agreement?.ProductSalesPrice), agreement?.CurrencyName].filter(Boolean).join(' ');
    const note = agreement?.Note || '';
    const threadName = agreement?.ThreadName || '';
    const deliverTime = fmtDate(agreement?.OrderDelivertime);
    const paymentTerm = agreement?.PaymentTerm || '';
    const shipmentPlace = agreement?.ShipmentPlace || '';

    return `<!DOCTYPE html><html><head><meta charset="utf-8" />
    <title>Müşteri Sözleşmesi</title>
    <style>
      ${templateCSS}
    </style>
    </head><body>
      ${renderPrintMarkup({ logoUrl, tarihStr, customerName, contactName, articleNo, weaveCode, weaveType, warpYarn, weftYarn, warpDensity, weftDensity, reedWidth, quantity, price, note, threadName, deliverTime, paymentTerm, shipmentPlace })}
    </body></html>`;
  };

  const handlePreview = () => {
    setPreviewOpen(true);
  };

  const handlePrint = () => {
    const html = buildTemplateHTML();
    const w = window.open('', '_blank');
    w.document.write(html);
    w.document.close();
    w.focus();
    setTimeout(() => {
      w.print();
      w.onafterprint = () => w.close();
    }, 400);
  };

  const handleExportExcel = () => {
    if (!agreement) return;
    const ctx = {
      logoUrl: `${window.location.origin}/logo.jpg`,
      tarihStr: new Date().toLocaleDateString('tr-TR'),
      customerName: agreement?.CustomerName || '',
      contactName: agreement?.ContactName || '',
      articleNo: agreement?.CustomerCode || '',
      weaveCode: agreement?.WeaveCode || '',
      weaveType: agreement?.WeaveTypeName || '',
      warpYarn: joinYarns(agreement?.WarpYarnInfoList),
      weftYarn: joinYarns(agreement?.WeftYarnInfoList),
      warpDensity: formatDensityDecimal(agreement?.WarpDensity),
      weftDensity: formatDensityDecimal(agreement?.WeftDensity),
      reedWidth: agreement?.ReedWidth || '',
      orderDate: fmtDate(agreement?.OrderDatetime),
      quantity: fmtNum(agreement?.ProductQuantity),
      price: [fmtPrice(agreement?.ProductSalesPrice), agreement?.CurrencyName].filter(Boolean).join(' '),
      note: agreement?.Note || '',
      threadName: agreement?.ThreadName || '',
      deliverTime: fmtDate(agreement?.OrderDelivertime),
      paymentTerm: agreement?.PaymentTerm || '',
      shipmentPlace: agreement?.ShipmentPlace || '',
    };
    const html = buildExcelHTMLFromCtx(ctx);
    const blob = new Blob([html], { type: 'application/vnd.ms-excel;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `musteri_sozlesmesi_${ctx.weaveCode || 'rapor'}_${new Date().toISOString().slice(0, 10)}.xls`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const resetForm = () => {
    setSelectedOrderGuid('');
    setReportRows([]);
    setError('');
  };

  return (
    <div className="customer-agreement-report">
      <CContainer fluid className="px-4">
        <div className="page-header mb-4">
          <h2 className="page-title">
            <i className="fas fa-file-contract"></i>
            {t('customerAgreementReport') || 'Müşteri Sözleşmesi Raporu'}
          </h2>
          <p className="page-subtitle text-muted">
            {t('selectAgreementDescription') || 'Sözleşme seçerek raporu görüntüleyebilir, yazdırabilir veya Excel formatında indirebilirsiniz.'}
          </p>
        </div>

        {error && (
          <CAlert color="danger" dismissible onClose={() => setError('')} className="mb-4">
            <i className="fas fa-exclamation-triangle me-2"></i>
            {error}
          </CAlert>
        )}

        <CRow className="mb-4 justify-content-center">
          <CCol xs={12} xl={10} xxl={8}>
            <CCard className="shadow-sm border-0 selection-card">
              <CCardHeader className="gradient-header text-white">
                <div className="d-flex align-items-center">
                  <i className="fas fa-search me-2"></i>
                  <h5 className="mb-0">{t('agreementSelection') || 'Sözleşme Seçimi'}</h5>
                </div>
              </CCardHeader>
              <CCardBody className="p-4">
                <CRow className="g-3">
                  <CCol xs={12} lg={6}>
                    <CFormLabel htmlFor="agreementSelect" className="form-label-required">
                      {t('selectAgreement') || 'Sözleşme Seçin'}
                    </CFormLabel>
                    <SearchableSelect
                      id="agreementSelect"
                      options={dropdownOptions}
                      value={selectedOrderGuid}
                      onChange={(v) => setSelectedOrderGuid(v)}
                      placeholder={loadingDropdown ? (t('loading') || 'Yükleniyor...') : 'Sipariş no ile  ara...'}
                      displayKey="text"
                      valueKey="value"
                      disabled={loadingDropdown}
                      onSearchTermChange={fetchDropdownByOrderNo}
                    />
                    {dropdownOptions.length > 0 && (
                      <small className="text-muted mt-1 d-block">
                        <i className="fas fa-info-circle me-1"></i>
                        {dropdownOptions.length} sözleşme mevcut
                      </small>
                    )}
                  </CCol>
                  <CCol xs={12} lg={6}>
                    <CFormLabel className="d-none d-lg-block" style={{ visibility: 'hidden' }}>
                      &nbsp;
                    </CFormLabel>
                    <div className="action-buttons-group">
                      <CButton
                        className="btn-report-generate"
                        onClick={handleGetReport}
                        disabled={!selectedOrderGuid || loadingReport || loadingDropdown}
                        title={t('generateReport') || 'Raporu Getir'}
                      >
                        {loadingReport ? (
                          <>
                            <CSpinner size="sm" className="me-2" />
                            {t('loading') || 'Yükleniyor...'}
                          </>
                        ) : (
                          <>
                            <i className="fas fa-file-alt me-2"></i>
                            {t('generateReport') || 'Raporu Getir'}
                          </>
                        )}
                      </CButton>
                      {(selectedOrderGuid || reportRows.length > 0) && (
                        <CButton
                          className="btn-clear"
                          onClick={resetForm}
                          disabled={loadingReport || loadingDropdown}
                          title={t('clear') || 'Temizle'}
                        >
                          <span className="clear-icon">×</span>
                          <span className="btn-text">{t('clear') || 'Temizle'}</span>
                        </CButton>
                      )}
                    </div>
                  </CCol>
                </CRow>
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>

        {agreement && (
          <>
            <CRow className="mb-4 justify-content-center">
              <CCol xs={12} xl={10} xxl={8}>
                <CCard className="shadow-sm border-0 details-card">
                  <CCardHeader className="details-header">
                    <div className="details-header-content">
                      <div className="details-info">
                        <h5 className="mb-2 details-title">
                          <i className="fas fa-file-contract me-2"></i>
                          {t('agreementDetails') || 'Sözleşme Detayları'}
                        </h5>
                        <div className="details-badges">
                          <CBadge className="detail-badge customer-badge">
                            <i className="fas fa-user me-1"></i>
                            {agreement.CustomerName}
                          </CBadge>
                          <CBadge className="detail-badge code-badge">
                            <i className="fas fa-barcode me-1"></i>
                            {agreement.WeaveCode}
                          </CBadge>
                          {agreement.OrderDatetime && (
                            <CBadge className="detail-badge date-badge">
                              <i className="fas fa-calendar me-1"></i>
                              {fmtDate(agreement.OrderDatetime)}
                            </CBadge>
                          )}
                        </div>
                      </div>
                      <div className="action-buttons-export">

                        <CButton
                          className="btn-action-print"
                          size="sm"
                          onClick={handlePrint}
                          disabled={!agreement}
                        >
                          <i className="fas fa-print me-1"></i>
                          <span className="btn-text">{t('print') || 'Yazdır'}</span>
                        </CButton>
                        <CButton
                          className="btn-action-excel"
                          size="sm"
                          onClick={handleExportExcel}
                          disabled={!agreement}
                        >
                          <i className="fas fa-file-excel me-1"></i>
                          <span className="btn-text">Excel</span>
                        </CButton>
                      </div>
                    </div>
                  </CCardHeader>
                </CCard>
              </CCol>
            </CRow>

            <CRow className="justify-content-center">
              <CCol xs={12} xl={10} xxl={8}>
                <CCard className="agreement-preview-card shadow">
                  <CCardBody className="p-0">
                    <div className="agreement-a4-preview two-page-preview">
                      <div dangerouslySetInnerHTML={{
                        __html: renderPreviewSinglePage({
                          logoUrl: `${window.location.origin}/logo.jpg`,
                          tarihStr: new Date().toLocaleDateString('tr-TR'),
                          customerName: agreement?.CustomerName || '',
                          contactName: agreement?.ContactName || '',
                          articleNo: agreement?.CustomerCode || '',
                          weaveCode: agreement?.WeaveCode || '',
                          weaveType: agreement?.WeaveTypeName || '',
                          warpYarn: joinYarns(agreement?.WarpYarnInfoList),
                          weftYarn: joinYarns(agreement?.WeftYarnInfoList),
                          warpDensity: formatDensityDecimal(agreement?.WarpDensity),
                          weftDensity: formatDensityDecimal(agreement?.WeftDensity),
                          reedWidth: agreement?.ReedWidth || '',
                          quantity: fmtNum(agreement?.ProductQuantity),
                          price: [fmtPrice(agreement?.ProductSalesPrice), agreement?.CurrencyName].filter(Boolean).join(' '),
                          note: agreement?.Note || '',
                          threadName: agreement?.ThreadName || '',
                          deliverTime: fmtDate(agreement?.OrderDelivertime),
                          paymentTerm: agreement?.PaymentTerm || '',
                          shipmentPlace: agreement?.ShipmentPlace || '',
                        })
                      }} />
                    </div>
                  </CCardBody>
                </CCard>
              </CCol>
            </CRow>


          </>
        )}
      </CContainer>
    </div>
  );
}

export default CustomerAgreementReport;

// Enhanced CSS for better visual design - Classic professional look with larger fonts
const templateCSS = `
  @page { size: A4; margin: 16mm; }
  * {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
    color-adjust: exact !important;
  }
  body { 
    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; 
    color: #333; 
    line-height: 1.55;
    margin: 0;
    padding: 0;
    font-size: 16px;
  }
  .agreement-wrap { 
    width: 100%; 
    border: 2px solid #000 !important; 
    border-radius: 4px;
    overflow: hidden;
    background: #ffffff;
  }
  .header-grid { 
    display: grid; 
    grid-template-columns: 100px 1fr 180px; 
    border-bottom: 2px solid #000 !important; 
    background: #ffffff;
  }
  .logo-cell { 
    border-right: 2px solid #000 !important; 
    display: flex; 
    align-items: center; 
    justify-content: center; 
    padding: 12px 8px; 
    background: #ffffff;
  }
  .title-cell { 
    display: flex; 
    align-items: center; 
    justify-content: center; 
    font-weight: 700; 
    font-size: 26px; 
    color: #000;
    text-align: center;
    padding: 12px;
  }
  .meta-cell { 
    border-left: 2px solid #000 !important; 
    padding: 8px; 
    background: #ffffff;
  }
  .meta-table { 
    width: 100%; 
    border-collapse: collapse; 
    font-size: 13px; 
  }
  .meta-table td { 
    border: 1px solid #000 !important; 
    padding: 6px 8px; 
    background: #ffffff;
    vertical-align: middle;
  }
  .meta-table strong {
    color: #000;
    font-weight: 600;
  }
  .customer-info-table {
    width: 100%; 
    border-collapse: collapse; 
    font-size: 16px;
    margin: 0;
  }
  .customer-info-table td {
    border: 1px solid #000 !important;
    padding: 10px 12px;
    background: #ffffff;
    vertical-align: middle;
  }
  .customer-info-table .label-cell {
    background: #f8f9fa;
    font-weight: 600;
    width: 15%;
    color: #000;
  }
  .customer-info-table .value-cell {
    background: #ffffff;
    color: #000;
  }
  .section { 
    border-left: 1px solid #000 !important;
    border-right: 1px solid #000 !important;
  }
  .row { 
    display: grid; 
    grid-template-columns: 220px 1fr; 
    border-bottom: 1px solid #000 !important;
  }
  .row .label { 
    border-right: 1px solid #000 !important; 
    font-weight: 600; 
    padding: 14px 16px; 
    background: #f8f9fa;
    color: #000;
    display: flex;
    align-items: center;
    font-size: 16px;
  }
  .row .value { 
    padding: 14px 16px; 
    background: #ffffff;
    color: #000;
    display: flex;
    align-items: center;
    min-height: 50px;
    font-size: 16px;
  }
  .notes { 
    min-height: 60px; 
    border-top: 2px solid #000 !important; 
    border-bottom: 2px solid #000 !important; 
    border-left: 1px solid #000 !important;
    border-right: 1px solid #000 !important;
    padding: 12px; 
    background: #ffffff;
  }
  .note-title { 
    font-weight: 700; 
    margin-bottom: 8px; 
    color: #000;
    font-size: 15px;
  }
  .terms { 
    font-size: 16px; 
    line-height: 1.5; 
    padding: 12px; 
    background: #f8f9fa;
    color: #333;
    border-left: 1px solid #000 !important;
    border-right: 1px solid #000 !important;
  }
  .terms > div {
    margin-bottom: 4px;
    padding-left: 8px;
    border-left: 2px solid #ddd !important;
  }
  .signatures { 
    display: grid; 
    grid-template-columns: 1fr 1fr; 
    border-top: 2px solid #000 !important; 
    border-left: 1px solid #000 !important;
    border-right: 1px solid #000 !important;
    border-bottom: 1px solid #000 !important;
  }
  .sig-cell { 
    border-left: 1px solid #000 !important; 
    padding: 20px 16px; 
    height: 100px; 
    text-align: center;
    background: #ffffff;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }
  .sig-cell:first-child { 
    border-left: none !important; 
    border-right: 1px solid #000 !important;
  }
  .sig-cell strong {
    color: #000;
    margin-bottom: 6px;
    font-size: 15px;
    font-weight: 700;
  }
  .muted { 
    color: #666; 
  }
  .small { 
    font-size: 12px; 
  }
  @media print {
    * {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
      color-adjust: exact !important;
    }
    .agreement-wrap, .header-grid, .logo-cell, .title-cell, .meta-cell,
    .meta-table td, .customer-info-table td, .section, .row, .row .label,
    .notes, .terms, .signatures, .sig-cell {
      border-color: #000 !important;
    }
  }
`;

// Returns inner markup (without html/head/body) to enable reuse in React preview
function renderTemplateMarkup(params) {
  const { logoUrl, tarihStr, customerName, contactName, articleNo, weaveCode, weaveType, warpYarn, weftYarn, warpDensity, weftDensity, reedWidth, quantity, price, note, threadName, deliverTime, paymentTerm, shipmentPlace } = params;
  const safeLogoUrl = sanitizeImageSrc(logoUrl);
  const safeTarihStr = escapeHtml(tarihStr || '');
  const safeCustomerName = escapeHtml(customerName || '');
  const safeContactName = escapeHtml(contactName || '');
  const safeNote = escapeHtml(note || '');
  const row = (l, v) => `<tr><td class="label-cell" style="width:220px;border:1px solid #000 !important;padding:12px 14px;background:#f8f9fa;font-weight:600;">${escapeHtml(l)}</td><td colspan="2" style="border:1px solid #000 !important;padding:12px 14px;background:#fff;">${escapeHtml(v || '')}</td></tr>`;
  return `
  <table class="agreement-table" style="width:100%;border-collapse:collapse;border:2px solid #000 !important;font-family:'Segoe UI',Tahoma,sans-serif;font-size:16px;">
    <tr>
      <td style="width:110px;border:1px solid #000 !important;padding:14px 10px;text-align:center;vertical-align:middle;background:#fff;">
        <img src="${safeLogoUrl}" alt="logo" style="max-height:65px;max-width:100px;object-fit:contain;"/>
      </td>
      <td style="border:1px solid #000 !important;padding:14px;text-align:center;font-weight:700;font-size:26px;background:#fff;">Sipariş Sözleşmesi</td>
      <td style="width:170px;border:1px solid #000 !important;padding:14px;background:#fff;"><strong>Tarih:</strong> ${safeTarihStr}</td>
    </tr>
    <tr>
      <td style="border:1px solid #000 !important;padding:12px 14px;background:#f8f9fa;font-weight:600;">Müşteri :</td>
      <td colspan="2" style="border:1px solid #000 !important;padding:0;background:#fff;">
        <table style="width:100%;border-collapse:collapse;">
          <tr>
            <td style="border:none;border-right:1px solid #000 !important;padding:12px 14px;">${safeCustomerName}</td>
          </tr>
        </table>
      </td>
    </tr>
    <tr>
      <td style="border:1px solid #000 !important;padding:12px 14px;background:#f8f9fa;font-weight:600;">Yetkili:</td>
      <td colspan="2" style="border:1px solid #000 !important;padding:0;background:#fff;">
        <table style="width:100%;border-collapse:collapse;">
          <tr>
            <td style="border:none;border-right:1px solid #000 !important;padding:12px 14px;">${safeContactName}</td>
          </tr>
        </table>
      </td>
    </tr>
    ${row('Artikel No:', articleNo)}
    ${row('Tip Kodu:', weaveCode)}
    ${row('Kalite-Örgü:', weaveType)}
    ${row('Çözgü İplik No:', warpYarn)}
    ${row('Çözgü Sıklık:', warpDensity)}
    ${row('Atkı İplik No:', weftYarn)}
    ${row('Atkı Sıklık:', weftDensity)}
    ${row('Tarak Eni:', reedWidth)}
    ${row('Sipariş Metresi:', quantity)}
    ${row('Termin :', deliverTime)}
    ${row('Fiyat :', price)}
    ${row('Karışım Oranı:', threadName)}
    ${row('Ödeme Vade:', paymentTerm)}
    ${row('Sevk Adresi :', shipmentPlace)}
    <tr>
      <td style="border:1px solid #000 !important;padding:10px 12px;background:#f8f9fa;font-weight:600;vertical-align:top;">NOT:</td>
      <td colspan="2" style="border:1px solid #000 !important;padding:10px 12px;background:#fff;min-height:60px;white-space:pre-wrap;">${safeNote}</td>
    </tr>
    <tr>
      <td colspan="3" style="border:1px solid #000 !important;padding:12px;background:#f8f9fa;font-size:16px;line-height:1.5;">
        <div style="margin-bottom:4px;">1- Sipariş teyidi ile geçerlilik kazanacaktır.</div>
        <div style="margin-bottom:4px;">2- Fiyatlarımız KDV dahil değildir.</div>
        <div style="margin-bottom:4px;">3- Satışlarımız Denizli teslimlidir. Nakliye ve sigorta alıcıya aittir.</div>
        <div style="margin-bottom:4px;">4- Kur farklı satışlarda vadesindeki TCMB döviz satış kuru geçerlidir.</div>
        <div style="margin-bottom:4px;">5- Geç ödemelerde aylık %4 vade farkı uygulanır. Karşılıksız çekler 7 gün içinde nakden tahsil edilir.</div>
        <div style="margin-bottom:4px;">6- Siparişi veren firma siparişi iptal etse dahi, çözülmüş miktarı almayı taahhüt eder.</div>
        <div style="margin-bottom:4px;">7- Bu satış sözleşmesi ile ilgili ihtilaflarda Denizli Mahkemeleri ve icra daireleri yetkilidir.</div>
        <div>8- Sipariş miktarının %5 i kadar eksik veya fazla sevk edilebilir.</div>
      </td>
    </tr>
    <tr>
      <td colspan="3" style="border:1px solid #000 !important;padding:0;background:#fff;">
        <table style="width:100%;border-collapse:collapse;">
          <tr>
            <td style="width:50%;border:none;border-right:1px solid #000 !important;padding:20px 16px;height:80px;text-align:center;"><strong>MÜŞTERİ</strong><br/><span style="font-size:12px;color:#666;">İMZA / KAŞE</span></td>
            <td style="width:50%;border:none;padding:20px 16px;height:80px;text-align:center;"><strong>FABCO TEKSTİL</strong><br/><span style="font-size:12px;color:#666;">İMZA/KAŞE</span></td>
          </tr>
        </table>
      </td>
    </tr>
  </table>`;
}

const toYarnLines = (html) => (html || '')
  .split('<br/>')
  .map(x => x.trim())
  .filter(Boolean);

const normalizeFixedYarnLines = (html, fixedCount = 8) => {
  const lines = toYarnLines(html).slice(0, fixedCount);
  while (lines.length < fixedCount) lines.push('&nbsp;');
  return lines;
};

const yarnLinesToHtml = (lines = []) => lines.join('<br/>');

// Print markup: compact single-page layout
function renderPrintMarkup(params) {
  const { logoUrl, tarihStr, customerName, contactName, articleNo, weaveCode, weaveType, warpYarn, weftYarn, warpDensity, weftDensity, reedWidth, quantity, price, note, threadName, deliverTime, paymentTerm, shipmentPlace } = params;
  const safeLogoUrl = sanitizeImageSrc(logoUrl);
  const safeTarihStr = escapeHtml(tarihStr || '');
  const safeCustomerName = escapeHtml(customerName || '');
  const safeContactName = escapeHtml(contactName || '');
  const safeNote = escapeHtml(note || '');

  const singleRow = (l, v) => `<tr>
    <td style="width:220px;border:1px solid #000 !important;padding:9px 11px;background:#f8f9fa;font-weight:600;">${escapeHtml(l)}</td>
    <td colspan="3" style="border:1px solid #000 !important;padding:9px 11px;background:#fff;">${escapeHtml(v || '')}</td>
  </tr>`;
  const pairRow = (l1, v1, l2, v2) => `<tr>
    <td style="width:220px;border:1px solid #000 !important;padding:9px 11px;background:#f8f9fa;font-weight:600;">${escapeHtml(l1)}</td>
    <td style="width:30%;border:1px solid #000 !important;padding:9px 11px;background:#fff;">${escapeHtml(v1 || '')}</td>
    <td style="width:200px;border:1px solid #000 !important;padding:9px 11px;background:#f8f9fa;font-weight:600;">${escapeHtml(l2)}</td>
    <td style="border:1px solid #000 !important;padding:9px 11px;background:#fff;">${escapeHtml(v2 || '')}</td>
  </tr>`;
  const yarnRow = (l, v) => `<tr>
    <td style="width:220px;border:1px solid #000 !important;padding:9px 11px;background:#f8f9fa;font-weight:600;vertical-align:top;">${escapeHtml(l)}</td>
    <td colspan="3" style="border:1px solid #000 !important;padding:8px 11px;background:#fff;min-height:56px;vertical-align:top;line-height:1.35;font-size:13px;">${v || ''}</td>
  </tr>`;

  const warpHtml = yarnLinesToHtml(normalizeFixedYarnLines(warpYarn, 4));
  const weftHtml = yarnLinesToHtml(normalizeFixedYarnLines(weftYarn, 4));

  return `
  <table class="agreement-table" style="width:100%;border-collapse:collapse;border:2px solid #000 !important;font-family:'Segoe UI',Tahoma,sans-serif;font-size:14px;">
    <tr>
      <td style="width:110px;border:1px solid #000 !important;padding:10px 8px;text-align:center;vertical-align:middle;background:#fff;">
        <img src="${safeLogoUrl}" alt="logo" style="max-height:56px;max-width:96px;object-fit:contain;"/>
      </td>
      <td colspan="2" style="border:1px solid #000 !important;padding:10px;text-align:center;font-weight:700;font-size:23px;background:#fff;">Sipariş Sözleşmesi</td>
      <td style="width:220px;border:1px solid #000 !important;padding:10px;background:#fff;"><strong>Tarih:</strong> ${safeTarihStr}</td>
    </tr>
    <tr>
      <td style="border:1px solid #000 !important;padding:9px 11px;background:#f8f9fa;font-weight:600;">Müşteri :</td>
      <td colspan="3" style="border:1px solid #000 !important;padding:9px 11px;background:#fff;">${safeCustomerName}</td>
    </tr>
    <tr>
      <td style="border:1px solid #000 !important;padding:9px 11px;background:#f8f9fa;font-weight:600;">Yetkili:</td>
      <td colspan="3" style="border:1px solid #000 !important;padding:9px 11px;background:#fff;">${safeContactName}</td>
    </tr>
    ${pairRow('Artikel No:', articleNo, 'Tip Kodu:', weaveCode)}
    ${pairRow('Kalite-Örgü:', weaveType, 'Tarak Eni:', reedWidth)}
    ${pairRow('Çözgü Sıklık:', warpDensity, 'Atkı Sıklık:', weftDensity)}
    ${pairRow('Sipariş Metresi:', quantity, 'Termin:', deliverTime)}
    ${pairRow('Fiyat:', price, 'Ödeme Vade:', paymentTerm)}
    ${singleRow('Karışım Oranı:', threadName)}
    ${singleRow('Sevk Adresi:', shipmentPlace)}
    ${yarnRow('Çözgü İplik No:', warpHtml)}
    ${yarnRow('Atkı İplik No:', weftHtml)}
    <tr>
      <td style="border:1px solid #000 !important;padding:8px 10px;background:#f8f9fa;font-weight:600;vertical-align:top;">NOT:</td>
      <td colspan="3" style="border:1px solid #000 !important;padding:8px 10px;background:#fff;min-height:76px;vertical-align:top;white-space:pre-wrap;line-height:1.35;font-size:13px;">${safeNote}</td>
    </tr>
    <tr>
      <td colspan="4" style="border:1px solid #000 !important;padding:8px 10px;background:#f8f9fa;font-size:12px;line-height:1.32;">
        <div style="margin-bottom:2px;">1- Sipariş teyidi ile geçerlilik kazanacaktır.</div>
        <div style="margin-bottom:2px;">2- Fiyatlarımız KDV dahil değildir.</div>
        <div style="margin-bottom:2px;">3- Satışlarımız Denizli teslimlidir. Nakliye ve sigorta alıcıya aittir.</div>
        <div style="margin-bottom:2px;">4- Kur farkı satışlarda vadesindeki TCMB döviz satış kuru geçerlidir.</div>
        <div style="margin-bottom:2px;">5- Geç ödemelerde aylık %4 vade farkı uygulanır. Karşılıksız çekler 7 gün içinde nakden tahsil edilir.</div>
        <div style="margin-bottom:2px;">6- Siparişi veren firma siparişi iptal etse dahi, çözülmüş miktarı almayı taahhüt eder.</div>
        <div style="margin-bottom:2px;">7- Bu satış sözleşmesi ile ilgili ihtilaflarda Denizli Mahkemeleri ve icra daireleri yetkilidir.</div>
        <div>8- Sipariş miktarının %5 i kadar eksik veya fazla sevk edilebilir.</div>
      </td>
    </tr>
    <tr>
      <td colspan="4" style="border:1px solid #000 !important;padding:0;background:#fff;">
        <table style="width:100%;border-collapse:collapse;">
          <tr>
            <td style="width:50%;border:none;border-right:1px solid #000 !important;padding:14px 10px;height:62px;text-align:center;"><strong>MÜŞTERİ</strong><br/><span style="font-size:11px;color:#666;">İMZA / KAŞE</span></td>
            <td style="width:50%;border:none;padding:14px 10px;height:62px;text-align:center;"><strong>FABCO TEKSTİL</strong><br/><span style="font-size:11px;color:#666;">İMZA / KAŞE</span></td>
          </tr>
        </table>
      </td>
    </tr>
  </table>`;
}

// Preview markup for dynamic pages (visual preview without print page break)
function renderPreviewSinglePage(params) {
  const { logoUrl, tarihStr, customerName, contactName, articleNo, weaveCode, weaveType, warpYarn, weftYarn, warpDensity, weftDensity, reedWidth, quantity, price, note, threadName, deliverTime, paymentTerm, shipmentPlace } = params;
  const safeLogoUrl = sanitizeImageSrc(logoUrl);
  const safeTarihStr = escapeHtml(tarihStr || '');
  const safeCustomerName = escapeHtml(customerName || '');
  const safeContactName = escapeHtml(contactName || '');
  const safeNote = escapeHtml(note || '');

  const singleRow = (l, v) => `<tr>
    <td style="width:220px;border:1px solid #000 !important;padding:9px 11px;background:#f8f9fa;font-weight:600;">${escapeHtml(l)}</td>
    <td colspan="3" style="border:1px solid #000 !important;padding:9px 11px;background:#fff;">${escapeHtml(v || '')}</td>
  </tr>`;
  const pairRow = (l1, v1, l2, v2) => `<tr>
    <td style="width:220px;border:1px solid #000 !important;padding:9px 11px;background:#f8f9fa;font-weight:600;">${escapeHtml(l1)}</td>
    <td style="width:30%;border:1px solid #000 !important;padding:9px 11px;background:#fff;">${escapeHtml(v1 || '')}</td>
    <td style="width:200px;border:1px solid #000 !important;padding:9px 11px;background:#f8f9fa;font-weight:600;">${escapeHtml(l2)}</td>
    <td style="border:1px solid #000 !important;padding:9px 11px;background:#fff;">${escapeHtml(v2 || '')}</td>
  </tr>`;
  const yarnRow = (l, v) => `<tr>
    <td style="width:220px;border:1px solid #000 !important;padding:9px 11px;background:#f8f9fa;font-weight:600;vertical-align:top;">${escapeHtml(l)}</td>
    <td colspan="3" style="border:1px solid #000 !important;padding:8px 11px;background:#fff;min-height:56px;vertical-align:top;line-height:1.35;font-size:13px;">${v || ''}</td>
  </tr>`;

  const warpHtml = yarnLinesToHtml(normalizeFixedYarnLines(warpYarn, 4));
  const weftHtml = yarnLinesToHtml(normalizeFixedYarnLines(weftYarn, 4));

  return `
  <div class="preview-page">
    <div class="page-label">Sayfa 1</div>
    <table class="agreement-table" style="width:100%;border-collapse:collapse;border:2px solid #000 !important;font-family:'Segoe UI',Tahoma,sans-serif;font-size:14px;">
      <tr>
        <td style="width:110px;border:1px solid #000 !important;padding:10px 8px;text-align:center;vertical-align:middle;background:#fff;">
          <img src="${safeLogoUrl}" alt="logo" style="max-height:56px;max-width:96px;object-fit:contain;"/>
        </td>
        <td colspan="2" style="border:1px solid #000 !important;padding:10px;text-align:center;font-weight:700;font-size:23px;background:#fff;">Sipariş Sözleşmesi</td>
        <td style="width:220px;border:1px solid #000 !important;padding:10px;background:#fff;"><strong>Tarih:</strong> ${safeTarihStr}</td>
      </tr>
      <tr>
        <td style="border:1px solid #000 !important;padding:9px 11px;background:#f8f9fa;font-weight:600;">Müşteri :</td>
        <td colspan="3" style="border:1px solid #000 !important;padding:9px 11px;background:#fff;">${safeCustomerName}</td>
      </tr>
      <tr>
        <td style="border:1px solid #000 !important;padding:9px 11px;background:#f8f9fa;font-weight:600;">Yetkili:</td>
        <td colspan="3" style="border:1px solid #000 !important;padding:9px 11px;background:#fff;">${safeContactName}</td>
      </tr>
      ${pairRow('Artikel No:', articleNo, 'Tip Kodu:', weaveCode)}
      ${pairRow('Kalite-Örgü:', weaveType, 'Tarak Eni:', reedWidth)}
      ${pairRow('Çözgü Sıklık:', warpDensity, 'Atkı Sıklık:', weftDensity)}
      ${pairRow('Sipariş Metresi:', quantity, 'Termin:', deliverTime)}
      ${pairRow('Fiyat:', price, 'Ödeme Vade:', paymentTerm)}
      ${singleRow('Karışım Oranı:', threadName)}
      ${singleRow('Sevk Adresi:', shipmentPlace)}
      ${yarnRow('Çözgü İplik No:', warpHtml)}
      ${yarnRow('Atkı İplik No:', weftHtml)}
      <tr>
        <td style="border:1px solid #000 !important;padding:8px 10px;background:#f8f9fa;font-weight:600;vertical-align:top;">NOT:</td>
        <td colspan="3" style="border:1px solid #000 !important;padding:8px 10px;background:#fff;min-height:76px;vertical-align:top;white-space:pre-wrap;line-height:1.35;font-size:13px;">${safeNote}</td>
      </tr>
      <tr>
        <td colspan="4" style="border:1px solid #000 !important;padding:8px 10px;background:#f8f9fa;font-size:12px;line-height:1.32;">
          <div style="margin-bottom:2px;">1- Sipariş teyidi ile geçerlilik kazanacaktır.</div>
          <div style="margin-bottom:2px;">2- Fiyatlarımız KDV dahil değildir.</div>
          <div style="margin-bottom:2px;">3- Satışlarımız Denizli teslimlidir. Nakliye ve sigorta alıcıya aittir.</div>
          <div style="margin-bottom:2px;">4- Kur farkı satışlarda vadesindeki TCMB döviz satış kuru geçerlidir.</div>
          <div style="margin-bottom:2px;">5- Geç ödemelerde aylık %4 vade farkı uygulanır. Karşılıksız çekler 7 gün içinde nakden tahsil edilir.</div>
          <div style="margin-bottom:2px;">6- Siparişi veren firma siparişi iptal etse dahi, çözülmüş miktarı almayı taahhüt eder.</div>
          <div style="margin-bottom:2px;">7- Bu satış sözleşmesi ile ilgili ihtilaflarda Denizli Mahkemeleri ve icra daireleri yetkilidir.</div>
          <div>8- Sipariş miktarının %5 i kadar eksik veya fazla sevk edilebilir.</div>
        </td>
      </tr>
      <tr>
        <td colspan="4" style="border:1px solid #000 !important;padding:0;background:#fff;">
          <table style="width:100%;border-collapse:collapse;">
            <tr>
              <td style="width:50%;border:none;border-right:1px solid #000 !important;padding:14px 10px;height:62px;text-align:center;"><strong>MÜŞTERİ</strong><br/><span style="font-size:11px;color:#666;">İMZA / KAŞE</span></td>
              <td style="width:50%;border:none;padding:14px 10px;height:62px;text-align:center;"><strong>FABCO TEKSTİL</strong><br/><span style="font-size:11px;color:#666;">İMZA / KAŞE</span></td>
            </tr>
          </table>
        </td>
      </tr>
    </table>
  </div>`;
}

function pairRow(label, value) {
  return `<div class="row"><div class="label">${escapeHtml(label)}</div><div class="value">${escapeHtml(value || '')}</div></div>`;
}

// Build a table-based HTML optimized for Excel rendering with larger fonts
function buildExcelHTMLFromCtx(ctx) {
  const html = `<!DOCTYPE html><html><head><meta charset="utf-8" />
    <title>Müşteri Sözleşmesi</title>
    <style>
      body{font-family:'Segoe UI',Arial,sans-serif;font-size:13px;margin:0;padding:0;background:#fff;}
      table{border-collapse:collapse;width:100%;font-family:'Segoe UI',Arial,sans-serif;font-size:13px;}
      td{border:1px solid #000;padding:8px 10px;vertical-align:middle;}
      .excel-page{margin-bottom:28px;}
      .excel-page-break{page-break-after:always;mso-page-break-after:always;}
      .agreement-table td{font-size:14px;}
      .muted{color:#666;}
    </style>
  </head><body>
  ${renderExcelMarkup(ctx)}
  </body></html>`;
  return html;
}

function renderExcelMarkup(params) {
  const { logoUrl, tarihStr, customerName, contactName, articleNo, weaveCode, weaveType, warpYarn, weftYarn, warpDensity, weftDensity, reedWidth, quantity, price, note, threadName, deliverTime, paymentTerm, shipmentPlace } = params;
  const safeLogoUrl = sanitizeImageSrc(logoUrl);
  const safeTarihStr = escapeHtml(tarihStr || '');
  const safeCustomerName = escapeHtml(customerName || '');
  const safeContactName = escapeHtml(contactName || '');
  const safeNote = escapeHtml(note || '');

  const singleRow = (l, v) => `<tr>
    <td style="width:220px;border:1px solid #000 !important;padding:9px 11px;background:#f8f9fa;font-weight:600;">${escapeHtml(l)}</td>
    <td colspan="3" style="border:1px solid #000 !important;padding:9px 11px;background:#fff;">${escapeHtml(v || '')}</td>
  </tr>`;
  const pairRow = (l1, v1, l2, v2) => `<tr>
    <td style="width:220px;border:1px solid #000 !important;padding:9px 11px;background:#f8f9fa;font-weight:600;">${escapeHtml(l1)}</td>
    <td style="width:30%;border:1px solid #000 !important;padding:9px 11px;background:#fff;">${escapeHtml(v1 || '')}</td>
    <td style="width:200px;border:1px solid #000 !important;padding:9px 11px;background:#f8f9fa;font-weight:600;">${escapeHtml(l2)}</td>
    <td style="border:1px solid #000 !important;padding:9px 11px;background:#fff;">${escapeHtml(v2 || '')}</td>
  </tr>`;
  const yarnRow = (l, v) => `<tr>
    <td style="width:220px;border:1px solid #000 !important;padding:9px 11px;background:#f8f9fa;font-weight:600;vertical-align:top;">${escapeHtml(l)}</td>
    <td colspan="3" style="border:1px solid #000 !important;padding:8px 11px;background:#fff;min-height:56px;vertical-align:top;line-height:1.35;font-size:13px;">${v || ''}</td>
  </tr>`;

  const warpHtml = yarnLinesToHtml(normalizeFixedYarnLines(warpYarn, 4));
  const weftHtml = yarnLinesToHtml(normalizeFixedYarnLines(weftYarn, 4));

  return `
    <div class="excel-page">
      <table class="agreement-table" style="width:100%;border-collapse:collapse;border:2px solid #000 !important;font-family:'Segoe UI',Tahoma,sans-serif;font-size:14px;">
        <tr>
          <td style="width:110px;border:1px solid #000 !important;padding:10px 8px;text-align:center;vertical-align:middle;background:#fff;">
            <img src="${safeLogoUrl}" alt="logo" style="max-height:56px;max-width:96px;object-fit:contain;"/>
          </td>
          <td colspan="2" style="border:1px solid #000 !important;padding:10px;text-align:center;font-weight:700;font-size:23px;background:#fff;">Sipariş Sözleşmesi</td>
          <td style="width:220px;border:1px solid #000 !important;padding:10px;background:#fff;"><strong>Tarih:</strong> ${safeTarihStr}</td>
        </tr>
        <tr>
          <td style="border:1px solid #000 !important;padding:9px 11px;background:#f8f9fa;font-weight:600;">Müşteri :</td>
          <td colspan="3" style="border:1px solid #000 !important;padding:9px 11px;background:#fff;">${safeCustomerName}</td>
        </tr>
        <tr>
          <td style="border:1px solid #000 !important;padding:9px 11px;background:#f8f9fa;font-weight:600;">Yetkili:</td>
          <td colspan="3" style="border:1px solid #000 !important;padding:9px 11px;background:#fff;">${safeContactName}</td>
        </tr>
        ${pairRow('Artikel No:', articleNo, 'Tip Kodu:', weaveCode)}
        ${pairRow('Kalite-Örgü:', weaveType, 'Tarak Eni:', reedWidth)}
        ${pairRow('Çözgü Sıklık:', warpDensity, 'Atkı Sıklık:', weftDensity)}
        ${pairRow('Sipariş Metresi:', quantity, 'Termin:', deliverTime)}
        ${pairRow('Fiyat:', price, 'Ödeme Vade:', paymentTerm)}
        ${singleRow('Karışım Oranı:', threadName)}
        ${singleRow('Sevk Adresi:', shipmentPlace)}
        ${yarnRow('Çözgü İplik No:', warpHtml)}
        ${yarnRow('Atkı İplik No:', weftHtml)}
        <tr>
          <td style="border:1px solid #000 !important;padding:8px 10px;background:#f8f9fa;font-weight:600;vertical-align:top;">NOT:</td>
          <td colspan="3" style="border:1px solid #000 !important;padding:8px 10px;background:#fff;min-height:76px;vertical-align:top;white-space:pre-wrap;line-height:1.35;font-size:13px;">${safeNote}</td>
        </tr>
        <tr>
          <td colspan="4" style="border:1px solid #000 !important;padding:8px 10px;background:#f8f9fa;font-size:12px;line-height:1.32;">
            <div style="margin-bottom:2px;">1- Sipariş teyidi ile geçerlilik kazanacaktır.</div>
            <div style="margin-bottom:2px;">2- Fiyatlarımız KDV dahil değildir.</div>
            <div style="margin-bottom:2px;">3- Satışlarımız Denizli teslimlidir. Nakliye ve sigorta alıcıya aittir.</div>
            <div style="margin-bottom:2px;">4- Kur farkı satışlarda vadesindeki TCMB döviz satış kuru geçerlidir.</div>
            <div style="margin-bottom:2px;">5- Geç ödemelerde aylık %4 vade farkı uygulanır. Karşılıksız çekler 7 gün içinde nakden tahsil edilir.</div>
            <div style="margin-bottom:2px;">6- Siparişi veren firma siparişi iptal etse dahi, çözülmüş miktarı almayı taahhüt eder.</div>
            <div style="margin-bottom:2px;">7- Bu satış sözleşmesi ile ilgili ihtilaflarda Denizli Mahkemeleri ve icra daireleri yetkilidir.</div>
            <div>8- Sipariş miktarının %5 i kadar eksik veya fazla sevk edilebilir.</div>
          </td>
        </tr>
        <tr>
          <td colspan="4" style="border:1px solid #000 !important;padding:0;background:#fff;">
            <table style="width:100%;border-collapse:collapse;">
              <tr>
                <td style="width:50%;border:none;border-right:1px solid #000 !important;padding:14px 10px;height:62px;text-align:center;"><strong>MÜŞTERİ</strong><br/><span class="muted" style="font-size:11px;">İMZA / KAŞE</span></td>
                <td style="width:50%;border:none;padding:14px 10px;height:62px;text-align:center;"><strong>FABCO TEKSTİL</strong><br/><span class="muted" style="font-size:11px;">İMZA / KAŞE</span></td>
              </tr>
            </table>
          </td>
        </tr>
      </table>
    </div>`;
}





